import {
	Box,
	createStyles,
	Flex,
	getStylesRef,
	ScrollArea,
	Stack,
	UnstyledButton,
} from '@mantine/core';
import { hideNotification, showNotification } from '@mantine/notifications';
import { trackAnonymousEvent } from '@repo/common/analytics';
import { Icon, Text, Title } from '@repo/foundations';
import { transition } from '@repo/theme/primitives';
import axios from 'axios';
import { map } from 'lodash-es';
import { useCallback, useState } from 'react';
import type { IWorkspace } from '../../api';
import { joinWorkspace } from '../../api';
import { AuthCard } from './AuthCard';
import { AuthLayoutBase } from './AuthLayouts/AuthLayoutBase';
import {
	anonymousId,
	EMAIL_VERIFICATION_LOCAL_STORAGE_KEY,
	referrer,
} from './AuthStepEmailComponent.constants';
import { AuthStepWorkspaceJoinRow } from './AuthStepWorkspaceJoinRow';
import { WORKSPACE_TAB, WorkspaceTab } from './consts';

const useStyles = createStyles((theme) => ({
	displayOnHover: {
		ref: getStylesRef('displayOnHover'),
		opacity: 0,
		position: 'relative',
		left: `-${theme.other.space[3]}px`,
		...transition('duration-300', 'ease')('opacity'),
		...transition('duration-300', 'ease')('left'),
	},
	noWorkspacesMessage: {
		textAlign: 'center',
		padding: theme.spacing.xl,
	},
	wrapper: {
		flexShrink: 0,
		backgroundColor: theme.other.getColor('fill/primary/default'),
		padding: theme.spacing.xs,
		borderRadius: theme.other.space[3],
		boxShadow: theme.shadows.md,
		'&:hover, &:focus': {
			backgroundColor: theme.other.getColor('fill/primary/active'),
		},
		[`&:hover .${getStylesRef('displayOnHover')}, &:focus .${getStylesRef(
			'displayOnHover'
		)}`]: {
			opacity: 1,
			left: 0,
		},
		'&:disabled': {
			opacity: 0.6,
			cursor: 'not-allowed',
		},
	},
}));

export interface AuthStepWorkspaceJoinProps {
	workspaces: IWorkspace[];
	setActiveWorkspaceTab?: (tab: WorkspaceTab) => void;
}

export function AuthStepWorkspaceJoin({
	workspaces,
	setActiveWorkspaceTab,
}: AuthStepWorkspaceJoinProps) {
	const { classes, theme } = useStyles();
	const [loading, setLoading] = useState(false);
	const [loadingWorkspaceId, setLoadingWorkspaceId] = useState<string>('');
	const hasWorkspaces = workspaces.length > 0;

	const handleJoinWorkspace = useCallback(async (id: string) => {
		trackAnonymousEvent(
			'onboarding/workspace/join',
			{
				path: window.location.pathname,
				anonymous_id: anonymousId(),
				referrer: referrer(),
			},
			localStorage.getItem(EMAIL_VERIFICATION_LOCAL_STORAGE_KEY) ?? ''
		);
		setLoading(true);
		setLoadingWorkspaceId(id);
		try {
			await joinWorkspace(id);
			// We use `window.open` rather than `navigate` in this context in order to
			// trigger a reload which causes the workspace information to be refreshed
			// with the most up to date information about user's workspace membership.
			window.open('/onboarding/confirm', '_self');
		} catch (error: unknown) {
			let errorMessage = 'Failed to join workspace. Please try again.';

			if (axios.isAxiosError(error)) {
				const responseData = error.response?.data;
				if (responseData) {
					if (
						responseData.non_field_errors &&
						responseData.non_field_errors.length > 0
					) {
						[errorMessage] = responseData.non_field_errors;
					} else if (
						responseData.workspace &&
						responseData.workspace.length > 0
					) {
						[errorMessage] = responseData.workspace;
					}
				}
			}

			hideNotification('workspace-join-error');

			showNotification({
				id: 'workspace-join-error',
				title: 'Cannot Join Workspace',
				message: errorMessage,
				color: 'red',
				autoClose: 5000,
			});
		} finally {
			setLoading(false);
			setLoadingWorkspaceId('');
		}
	}, []);

	return (
		<AuthLayoutBase>
			<AuthCard>
				<Stack spacing="xl">
					<Title order={1}>Join a workspace</Title>
					{hasWorkspaces ? (
						<ScrollArea.Autosize mah={350} type="auto">
							<Stack spacing="sm" p="sm">
								{map(workspaces, (workspace) => {
									const beingJoined = loadingWorkspaceId === workspace.id;
									return (
										<AuthStepWorkspaceJoinRow
											key={workspace.id}
											disabled={loading}
											beingJoined={beingJoined}
											workspace={workspace}
											onJoinClick={() => handleJoinWorkspace(workspace.id)}
										/>
									);
								})}
								{setActiveWorkspaceTab && (
									<UnstyledButton
										disabled={loading}
										onClick={() => setActiveWorkspaceTab(WORKSPACE_TAB.CREATE)}
										className={classes.wrapper}
									>
										<Flex
											direction="row"
											justify="space-between"
											align="center"
										>
											<Flex direction="row" gap="sm" align="center">
												<Box p={theme.spacing.xs}>
													<Icon
														name="plus"
														color="icon/secondary/default"
														size="md"
													/>
												</Box>
												<Text
													lineClamp={1}
													size="sm"
													weight="bold"
													color="text/primary/default"
												>
													Create new workspace...
												</Text>
											</Flex>
											{!loading && (
												<Icon
													name="arrowRight"
													color="icon/primary/default"
													className={classes.displayOnHover}
												/>
											)}
										</Flex>
									</UnstyledButton>
								)}
							</Stack>
						</ScrollArea.Autosize>
					) : (
						<Box className={classes.noWorkspacesMessage}>
							<Text size="sm" color="text/secondary/default">
								No workspaces available. Please contact your administrator to
								get access to a workspace.
							</Text>
						</Box>
					)}
				</Stack>
			</AuthCard>
		</AuthLayoutBase>
	);
}
